import {
  Grid,
  Text,
  TextInput,
  Paper,
  Select,
  TagsInput,
  Tooltip,
  rem,
  Group,
  Autocomplete,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { Commune, CommonHash } from '@types';
import { format, validate } from 'rut.js';
import { useCommunes } from '@api/communes';
import { useCompaniesByTaxId, useCompany } from '@api/companies';
import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { exportType, receiptType, noteType, purchaseType } from './formUtils';
import { useCountries } from '@api/countries';

/** RUT only — Mantine Autocomplete inserts `option.label` into the input after picking an option. */
function normalizeTaxId(raw: string): string {
  if (!raw) return '';
  const portion = raw.split('|')[0].trim();
  if (!portion) return '';
  return format(portion, { dots: false });
}

function findReceiverRow(
  rows: CommonHash[] | undefined,
  normalizedTaxId: string,
  legalNameHint?: string,
): CommonHash | undefined {
  if (!rows?.length || !normalizedTaxId) return undefined;
  const matches = rows.filter((r) => normalizeTaxId(String(r.tax_id)) === normalizedTaxId);
  if (matches.length === 0) return undefined;
  if (matches.length === 1) return matches[0];
  if (legalNameHint) {
    const byName = matches.find((r) => r.legal_name === legalNameHint);
    if (byName) return byName;
  }
  return matches[0];
}

function Receiver({
  form,
  documentHydrated,
  expectedCompanyTaxId,
}: {
  form: UseFormReturnType<any>;
  /** Avoid treating async document hydration as a RUT change (draft edit). */
  documentHydrated: boolean;
  /** Draft payload RUT — baseline waits until `form.company.tax_id` matches (parent `setValues` applied). */
  expectedCompanyTaxId?: string | null;
}) {
  const queryClient = useQueryClient();
  const [debouncedTaxId] = useDebouncedValue(form.values.company.tax_id, 400);
  const { data: receiversOptions } = useCompaniesByTaxId(debouncedTaxId);
  const { data: countries } = useCountries();
  const { data: communes } = useCommunes();

  const normalizedTaxForDetailQuery = normalizeTaxId(form.values.company.tax_id);
  const shouldFetchReceiverDetail =
    Boolean(normalizedTaxForDetailQuery) && validate(normalizedTaxForDetailQuery);

  const { data: receiverDetails } = useCompany(normalizedTaxForDetailQuery || undefined, {
    enabled: shouldFetchReceiverDetail,
  });

  const optionRowByValue = useMemo(() => {
    const map = new Map<string, CommonHash>();
    receiversOptions?.data?.forEach((row: CommonHash, index: number) => {
      const taxId = String(row.tax_id);
      const uniqueValue =
        row.id != null && row.id !== ''
          ? `id:${row.id}`
          : `${taxId}__${index}__${encodeURIComponent(String(row.legal_name ?? ''))}`;
      map.set(uniqueValue, row);
    });
    return map;
  }, [receiversOptions]);

  /** Unique `value` keys (duplicate RUT allowed); `label` must stay as formatted RUT only — Mantine copies label into the input. */
  const autocompleteData = useMemo(
    () =>
      receiversOptions?.data?.map((row: CommonHash, index: number) => {
        const taxId = String(row.tax_id);
        const uniqueValue =
          row.id != null && row.id !== ''
            ? `id:${row.id}`
            : `${taxId}__${index}__${encodeURIComponent(String(row.legal_name ?? ''))}`;
        return {
          value: uniqueValue,
          label: normalizeTaxId(taxId),
        };
      }) ?? [],
    [receiversOptions],
  );

  const transformedCountries = countries?.data.map((country) => {
    return {
      label: country.name,
      value: String(country.id),
    };
  });
  const transformedCommunes = communes?.data.map((commune: Commune) => {
    return {
      label: commune.name,
      value: commune.name,
    };
  });

  const applyReceiverRowFromSearch = (row: CommonHash) => {
    const tid = normalizeTaxId(String(row.tax_id));
    const nextLegal = row.legal_name ?? '';
    const prevTid = normalizeTaxId(form.values.company.tax_id);
    const prevLegal = String(form.values.company.legal_name ?? '').trim();
    const receptorChanged = tid !== prevTid || nextLegal.trim() !== prevLegal;

    form.setFieldValue('company.tax_id', tid);
    form.setFieldValue('company.legal_name', nextLegal);

    // Re-picking the same option must not wipe giro/dirección: cached company detail won't re-run merge effect.
    if (!receptorChanged) {
      return;
    }

    form.setFieldValue('company.activity', '');
    form.setFieldValue('company.address', '');
    form.setFieldValue('company.commune', '');
    if (tid && validate(tid)) {
      void queryClient.invalidateQueries({ queryKey: ['company', tid] });
    }
  };

  const onBlurTax = (value: string) => {
    if (value === '') return;
    form.setFieldValue('company.tax_id', normalizeTaxId(value));
  };

  const baselineEstablished = useRef(false);
  const prevNormalizedTaxIdRef = useRef<string>('');

  /** When the normalized RUT changes (new receptor), sync list row and clear detail fields before `/companies/:rut` fills them. Baseline is taken once the page data is ready so draft loads do not clear receptor fields. */
  useEffect(() => {
    if (!documentHydrated) return;

    const normalized = normalizeTaxId(form.values.company.tax_id);

    if (!baselineEstablished.current) {
      if (expectedCompanyTaxId) {
        const expected = normalizeTaxId(String(expectedCompanyTaxId));
        if (expected && normalized !== expected) {
          return;
        }
      }
      baselineEstablished.current = true;
      prevNormalizedTaxIdRef.current = normalized;
      return;
    }

    if (prevNormalizedTaxIdRef.current === normalized) {
      return;
    }
    prevNormalizedTaxIdRef.current = normalized;

    if (!normalized) return;

    const row = findReceiverRow(
      receiversOptions?.data,
      normalized,
      form.values.company.legal_name as string | undefined,
    );
    if (!row) return;

    form.setFieldValue('company.legal_name', row.legal_name ?? '');
    form.setFieldValue('company.activity', '');
    form.setFieldValue('company.address', '');
    form.setFieldValue('company.commune', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setFieldValue stable; avoid reruns from `form`
  }, [documentHydrated, expectedCompanyTaxId, form.values.company.tax_id, receiversOptions]);

  useEffect(() => {
    const apiTax = receiverDetails?.data?.tax_id;
    if (!receiverDetails?.data || apiTax === undefined) return;

    if (normalizeTaxId(form.values.company.tax_id) !== normalizeTaxId(String(apiTax))) return;

    const { activity, address, commune } = receiverDetails.data;
    if (activity) form.setFieldValue('company.activity', activity);
    if (address) form.setFieldValue('company.address', address);
    if (commune) form.setFieldValue('company.commune', commune);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- merge snapshot when receiverDetails updates
  }, [receiverDetails]);

  const contactEmailTooltip = (
    <Tooltip label="Se puede agregar un máximo de 3 emails. Apretar enter para agregarlo">
      <IconInfoCircle
        style={{ width: rem(16), height: rem(16), color: 'var(--mantine-color-grey-4)' }}
      />
    </Tooltip>
  );

  const directionAsterisk = () => {
    if (receiptType(form.values.document.type)) {
      if (
        form.values.document.service_indicator === '1' ||
        form.values.document.service_indicator === '2'
      ) {
        return true;
      } else {
        return false;
      }
    } else if (noteType(form.values.document.type)) {
      return false;
    } else {
      return true;
    }
  };

  const taxInputProps = form.getInputProps('company.tax_id');

  return (
    <Paper shadow="xl" p="lg">
      <Text fw={700} mb={10} fz={20}>
        Receptor
      </Text>
      <Grid>
        {!exportType(form.values.document.type) && (
          <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
            <Autocomplete
              withAsterisk
              label="RUT"
              placeholder="RUT"
              {...taxInputProps}
              onBlur={(e) => {
                taxInputProps.onBlur?.(e);
                onBlurTax(e.target.value);
              }}
              data={autocompleteData}
              filter={({ options }) => options}
              renderOption={({ option }) => {
                const row = optionRowByValue.get(option.value);
                return row ? `${row.tax_id} | ${row.legal_name}` : option.value;
              }}
              onOptionSubmit={(uniqueKey) => {
                const row = optionRowByValue.get(uniqueKey);
                if (row) applyReceiverRowFromSearch(row);
              }}
            />
          </Grid.Col>
        )}
        {exportType(form.values.document.type) ||
        (purchaseType(form.values.document.type) && form.values.company.tax_id === '55555555-5') ? (
          <Grid.Col span={{ base: 12, md: 7 }}>
            <TextInput
              withAsterisk
              label="Nombre"
              placeholder="Nombre"
              maxLength={100}
              {...form.getInputProps('document.company_additional_info')}
            />
          </Grid.Col>
        ) : (
          <>
            <Grid.Col span={{ base: 12, md: 6, lg: 5 }}>
              <TextInput
                withAsterisk
                label="Razón Social"
                placeholder="Razón Social"
                maxLength={100}
                {...form.getInputProps('company.legal_name')}
              />
            </Grid.Col>
            {!receiptType(form.values.document.type) && (
              <Grid.Col span={{ base: 12, md: 6, lg: 5 }}>
                <TextInput
                  withAsterisk={!exportType(form.values.document.type)}
                  label="Giro"
                  placeholder="Giro"
                  maxLength={40}
                  {...form.getInputProps('company.activity')}
                />
              </Grid.Col>
            )}
          </>
        )}
        <Grid.Col span={{ base: 12, md: 6, lg: 5 }}>
          <TextInput
            withAsterisk={directionAsterisk()}
            label="Dirección"
            placeholder="Dirección"
            maxLength={70}
            {...form.getInputProps('company.address')}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 4 }}>
          {exportType(form.values.document.type) ? (
            <Select
              withAsterisk={!exportType(form.values.document.type)}
              label="Nacionalidad"
              placeholder="Nacionalidad"
              data={transformedCountries}
              {...form.getInputProps('document.nationality')}
              value={form.values.document.nationality}
              onChange={(value) => form.setFieldValue('document.nationality', value)}
              searchable
            />
          ) : (
            <Group style={{ position: 'relative' }}>
              <Select
                withAsterisk={directionAsterisk()}
                label="Comuna"
                placeholder="Comuna"
                data={transformedCommunes}
                {...form.getInputProps('company.commune')}
                value={form.values.company.commune}
                onChange={(value) => form.setFieldValue('company.commune', value)}
                searchable
                styles={{
                  root: {
                    width: '100%',
                  },
                }}
              />
              <Tooltip label="Seleccionar Extranjero si la comuna/ciudad es de otro país">
                <IconInfoCircle
                  style={{
                    width: rem(16),
                    height: rem(16),
                    color: 'var(--mantine-color-gray-6)',
                    position: 'absolute',
                    top: '5px',
                    left: '70px',
                  }}
                />
              </Tooltip>
            </Group>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
          <TagsInput
            label="Email de contacto"
            placeholder="Email"
            // maxLength={80}
            maxTags={3}
            rightSection={contactEmailTooltip}
            clearable
            {...form.getInputProps('document.contact_email')}
            value={form.values.document.contact_email}
            splitChars={[',', ' ', '|', ';']}
          />
        </Grid.Col>
      </Grid>
    </Paper>
  );
}

export default Receiver;
